import {
  enumBoxSubTypes,
  enumChartElement,
  enumDropType,
  enumFillMode,
  enumOverrideType,
  enumPanelBorderStyle,
  enumSubChartFlag,
  kRed,
  kRoundRectRadius
} from "../constants";
import { QDragPanelDraggerBoxElement } from "./QDragPanelDraggerBoxElement";
import { QElement } from "./QElement";
import { QElementDragPanel } from "./QElementDragPanel";

export class QBoxDraggerPanel extends QElementDragPanel {
  constructor() {
    super();
    this.fDestParking = null;
    this.fDestSubcharts = null;
    this.fShiftDown = null;
    this.fOverParkingPanel = null;
    this.fOverTalentPanel = null;
    this.fDestElement = null;
    this.fNoHit = true;
    this.fAlpha = 0.5;
    this.fDBObj = null;
    this.fRenderPhoenix = false;
  }

  InitializeDragExtras() {
    this.fDestParking = this.fDestPanel.fStagingPanel;
    this.fDestSubcharts = this.fDestPanel.fSubchartPanel;
    var zLayer = 2; // The zLayer of the Profile Panel. The BoxDragger should be +1 more than that.
    if (this.fDestSubcharts) zLayer = this.fDestSubcharts.GetZLayer();
    if (this.fDestParking) zLayer = this.fDestParking.GetZLayer();
    if (zLayer != null) this.fOverrideZLayer = zLayer + 1;
    this.fOverParkingPanel = false;
    this.fOverTalentPanel = false;
    this.fDestPanel.fDragSource = this.fSourceElement;
    var dbObj = this.fSourceElement.GetDataObj();
    if (dbObj != null) {
      this.fDBObj = dbObj.Clone(true);
      // Intentionally not using SetHolderList, that would update the reference from the holders to this element as the parent position
      this.fDBObj.fHolderList = dbObj.GetHolderList();
      this.fDBObj.fParentPosition = dbObj.GetParentPosition();
    }

    // Spawn the HTML element box
    const wsMgr = this.fWSMgr;
    const canvas = wsMgr.fCanvas;
    const rect = canvas.getBoundingClientRect();

    wsMgr.BroadcastTopic("dragStart", {
      panel: this,
      event: {
        clientX: this.fDragStartx + rect.left,
        clientY: this.fDragStarty + rect.top
      },
      dimensions: this.fSourceElement
        ? {
            width: this.fSourceElement.GetWidth(),
            height: this.fSourceElement.GetHeight()
          }
        : null,
      boxShape: this.fSourceElement?.GetLayout()?.GetBoxShapeType() || null
    });
  }

  StartDragInteraction() {
    this.fDestPanel.SetDropMode(true);
  }

  CreateDragElement() {
    var boxElement = new QDragPanelDraggerBoxElement();
    boxElement.InitPanelDraggerBox(this);
    boxElement.SizeElement(
      this.fSourceElement.GetWidth() * this.fDestPanel.fScaleFactor,
      this.fSourceElement.GetHeight() * this.fDestPanel.fScaleFactor
    );
    return boxElement;
  }

  SetBasicPanelUIProperties() {
    this.SetBorderThickness(2);
    this.SetBorderStyle(enumPanelBorderStyle.roundBorder);
    this.SetBorderRadius(kRoundRectRadius);
    this.SetBorderColor("#7EBFFF");
    this.SetFillColor("#CAE5FF");
    this.SetFillMode(enumFillMode.solidFill);

    if (this.fShowValidTargetIndicator) this.SetBorderColor(kRed);
  }

  HighlightParkingPanel(overParking) {
    if (overParking != this.fOverParkingPanel) {
      this.fOverParkingPanel = overParking;
      var panelRef = this.fDestParking;
      //panelRef.HighlightPanel(overParking);
      var fillColor = this.fUIColors.GetPrimaryPanelFillColor();
      var highlightFillColor = "#CAE5FF";
      if (overParking) {
        var se = this.fSourceElement;
        if (se.BottomOfSubChartBreak(true)) fillColor = highlightFillColor;
        else fillColor = highlightFillColor;
      }
    }
  }

  HighlightSubchartPanel(overSubchart) {
    //I know it's a performance downgrade to suppress this if statement, but rendering is context sensitive on the subchart panel
    //if (overSubchart != this.fOverSubchartPanel)
    //{
    this.fOverSubchartPanel = overSubchart;
    var fillColor = "#FFFFFF";
    if (overSubchart) {
      var se = this.fDestElement;
      if (!se) fillColor = "#FBDBDF";
      else {
        fillColor = "#c6dffa";
        if (this.fDestSubcharts) {
          this.fDestSubcharts.fPortPanel.ClearSelection();
          this.fDestSubcharts.fPortPanel.DoSelectElement(se, true);
        }
      }
    }

    //this.fDestSubcharts.SetFillColor(fillColor);
    //}
  }

  HandleTargetHit(controlElement, x, y) {
    const hitTest = this.fWSMgr.FindHitElement(x, y, false);
    const docMgr = this.fWSMgr.GetDocMgr();
    const isPlan = docMgr.IsPlanChart() ?? false;

    var isValid = false;
    if (hitTest.fPanelHit == this.fDestPanel) {
      if (this.fSourceElement && this.fSourceElement.GetDataObj() == null) return false;

      this.fDestPanel.ClearSelection();
      var theElement = hitTest.fElementHit;
      var validElement = this.IsValidTarget(theElement);

      if (validElement) {
        // Prevent dropping over drill-up arrows
        if (isPlan && theElement.GetElementType() == enumChartElement.scArrow) {
          var parentBox = theElement.fChartBox;
          const isDrillUp = parentBox?.GetSubChartFlag() == enumSubChartFlag.drillup ?? false;
          if (isDrillUp) {
            return false;
          }
        }

        theElement = this.ResolveDragOver(theElement);
        this.fDestPanel.DragOver(theElement, x);

        isValid = this.fDestPanel.ValidMove(this.fDestPanel.GetDropType());
      } else {
        this.fDestPanel.ClearDragOver();
      }

      this.HighlightParkingPanel(false);
      this.HighlightSubchartPanel(false);
    } else if (isPlan) {
      //Dragging over parking elements or subchart panel is invalid for boxes in planning.
      if (this.fDestPanel.GetDragOver() != null) {
        this.fDestPanel.ClearDragOver();
        this.fDestPanel.ClearSelection();
      }
      return false;
    } else {
      if (this.fDestPanel.GetDragOver() != null) {
        this.fDestPanel.ClearDragOver();
        this.fDestPanel.ClearSelection();
      }

      var highlightParking = false;
      var highlightSubcharts = false;
      this.fDestElement = null;
      if (this.fDestSubcharts && hitTest.fPanelHit == this.fDestSubcharts.fPortPanel) {
        highlightSubcharts = true;
        this.fDestElement = hitTest.fElementHit;
      }

      isValid = highlightParking || highlightSubcharts;

      this.HighlightParkingPanel(highlightParking);
      this.HighlightSubchartPanel(highlightSubcharts);
    }

    return isValid;
  }

  ResolveDragOver(element) {
    const docMgr = this.fWSMgr.GetDocMgr();

    if (element.GetElementType() == enumChartElement.boxCell) {
      return element.fParentBox;
    }

    if (docMgr.IsPlanChart() && element.GetElementType() == enumChartElement.textBox) {
      const layout = element.GetLayout();
      if (layout.GetBoxSubType() === enumBoxSubTypes.holderAreaBox) {
        const pageNode = element["nodeRef"];
        const containerNode = pageNode?.GetContainerNode();
        return containerNode.GetChartBox();
      }
    }

    return element;
  }

  IsValidTarget(element) {
    if (element == null) {
      return false;
    }

    if (element.GetElementType() == enumChartElement.textBox) {
      const layout = element.GetLayout();
      if (layout.GetBoxSubType() === enumBoxSubTypes.holderAreaBox) {
        return true;
      }
    }

    return [enumChartElement.chartBox, enumChartElement.scArrow, enumChartElement.boxCell].includes(
      element.GetElementType()
    );
  }

  DragComplete() {
    if (this.fOverParkingPanel) {
      if (!this.fSourceElement.BottomOfSubChartBreak(true)) {
        const w = this.fWSMgr;

        const dbObj = this.fSourceElement.GetDataObj();
        const objDB = dbObj.GetParentClass().GetObjDB();
        const doc = objDB.GetDocRef();

        w.StartCheckPoint();
        doc.MoveToStaging(dbObj);
        w.EndCheckPoint();

        w.ReloadCurrentSubChart();
        w.PrepRenderWS();
        w.RenderWS();
      }
    } else if (this.fOverSubchartPanel) {
      if (!this.fSourceElement.BottomOfSubChartBreak(true)) {
        var w = this.fWSMgr;
        var dbObj = this.fSourceElement.GetDataObj();
        var scm = w.GetSubChartMgr();
        var doc = this.fWSMgr.GetDoc();
        var objDB = doc.GetObjDB();
        var parent = dbObj.GetParent();
        var container = this.fSourceElement.GetContainerNode();
        var targetSC = this.fDestElement.GetSubChart();
        var topObj = targetSC.GetTopDBObj();
        var topObjRef = targetSC.GetTopDBObjRef();
        var cursubchartID = scm.GetCurrentSubChartID();

        var validDrop =
          !objDB.CheckforCircularReference(dbObj, topObj) &&
          topObj &&
          scm.FindSCSubNodeIndex(topObj.GetObjectID(), cursubchartID) > -1;
        if (validDrop) {
          w.StartCheckPoint();
          this.fDestPanel.DoMoveBranch(enumDropType.drillunder, dbObj, topObj, -1);
          w.EndCheckPoint();
          this.fDestSubcharts.UpdatePanel();

          if (parent) {
            var parentBox = this.fDestPanel.GetChartBoxFromDBObj(parent);
            var parentNode = this.fDestPanel.GetPLMNode(parentBox);
          } else if (container) parentNode = container;

          if (parentNode) {
            this.fDestPanel.AutoFormat(parentNode);
            this.fDestPanel.RecalculateChartLayout(null);
          }
        }
      }
    } else if (this.fOverTalentPanel) {
    }

    this.HighlightParkingPanel(false);
    this.HighlightSubchartPanel(false);
    this.fDestPanel.SetKeyboardTargetToCanvas();
  }

  GetDBObjClone() {
    return this.fDBObj;
  }

  GetMoveOrphanList(sourceElement, srcPanel) {
    /*
	var selectedElements = srcPanel.GetSelectionList();
	if (selectedElements == null)
		selectedElements = new Array();

	var index = selectedElements.indexOf(sourceElement);
	if (index == -1)
		selectedElements.push(sourceElement);

	numSelected = selectedElements.length;

	var fileList = new Array();
	for (var i=0;i<numSelected;i++)
	{
		var aFile = selectedElements[i].GetFile();
		fileList.push(aFile.GetFileName(true));
	}

	return fileList;
	*/
  }
}
